import { useState, useRef, useEffect, useCallback } from 'react';
import { useToast } from '../../../hooks/useToast';
import { createShipment, generateTrackingId } from '../../../lib/shipmentsService';
import { supabase } from '../../../lib/supabase';
import { useSiteSettings } from '../../../contexts/SiteSettingsContext';
import type { Shipment } from '../../../mocks/shipments';
import ShippingLabel, { type LabelData } from './ShippingLabel';

const EDGE_FN_URL = 'https://ttvxezxhcdxwsajhcjtl.supabase.co/functions/v1/send-notification';

async function sendCreationNotification(shipment: Shipment, siteUrl: string) {
  const email = shipment.receiver?.email;
  if (!email) return;
  try {
    const statusLabel = 'Shipment Created';
    await fetch(EDGE_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        trackingId: shipment.id,
        receiverEmail: email,
        receiverName: shipment.receiver.name,
        newStatus: shipment.status,
        statusLabel,
        currentLocation: `${shipment.origin.city}, ${shipment.origin.country}`,
        reason: `Your shipment has been registered and a tracking number has been assigned. You can use the link below to track your package at any time.`,
        estimatedDelivery: shipment.estimatedDelivery,
        origin: `${shipment.origin.city}, ${shipment.origin.country}`,
        destination: `${shipment.destination.city}, ${shipment.destination.country}`,
        siteUrl,
      }),
    });
  } catch {
    // notification failure is non-blocking
  }
}

async function sendAdminNotification(shipment: Shipment, siteUrl: string) {
  try {
    // Fetch all admin emails from the database
    const { data: admins } = await supabase
      .from('admin_users')
      .select('email')
      .not('email', 'is', null);

    const adminEmails = (admins ?? []).map((a: { email: string }) => a.email).filter(Boolean);
    if (adminEmails.length === 0) return;

    await fetch(EDGE_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        isAdminNotification: true,
        adminEmails,
        trackingId: shipment.id,
        senderName: shipment.sender.name,
        receiverName: shipment.receiver.name,
        receiverEmail: shipment.receiver.email,
        newStatus: shipment.status,
        statusLabel: 'Shipment Created',
        estimatedDelivery: shipment.estimatedDelivery,
        origin: `${shipment.origin.city}, ${shipment.origin.country}`,
        destination: `${shipment.destination.city}, ${shipment.destination.country}`,
        siteUrl,
        adminPanelUrl: `${siteUrl}/admin`,
      }),
    });
  } catch {
    // admin notification failure is non-blocking
  }
}

type NewShipment = {
  senderName: string;
  senderCity: string;
  receiverName: string;
  receiverCity: string;
  receiverEmail: string;
  originInput: string;
  destinationInput: string;
  description: string;
  weight: string;
  dimensions: string;
  service: string;
  estimatedDelivery: string;
  paymentMethod: string;
  declaredValue: string;
  status: string;
};

const EMPTY: NewShipment = {
  senderName: '',
  senderCity: '',
  receiverName: '',
  receiverCity: '',
  receiverEmail: '',
  originInput: '',
  destinationInput: '',
  description: '',
  weight: '',
  dimensions: '',
  service: 'Standard',
  estimatedDelivery: '',
  paymentMethod: 'Cash on Delivery (COD)',
  declaredValue: '',
  status: 'pending',
};

const STATUS_OPTIONS: { value: string; label: string; icon: string; color: string; bg: string; description: string }[] = [
  { value: 'pending',          label: 'Pending',          icon: 'ri-time-line',              color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',   description: 'Shipment registered, awaiting pickup' },
  { value: 'picked_up',        label: 'Picked Up',        icon: 'ri-hand-coin-line',         color: '#a78bfa', bg: 'rgba(167,139,250,0.12)',  description: 'Shipment picked up from sender' },
  { value: 'in_transit',       label: 'In Transit',       icon: 'ri-truck-line',             color: '#38bdf8', bg: 'rgba(56,189,248,0.12)',   description: 'Shipment is on its way' },
  { value: 'out_for_delivery', label: 'Out for Delivery', icon: 'ri-e-bike-line',            color: '#fb923c', bg: 'rgba(251,146,60,0.12)',   description: 'Shipment is out for final delivery' },
  { value: 'delivered',        label: 'Delivered',        icon: 'ri-checkbox-circle-line',   color: '#4ade80', bg: 'rgba(74,222,128,0.12)',   description: 'Shipment successfully delivered' },
  { value: 'on_hold',          label: 'On Hold',          icon: 'ri-pause-circle-line',      color: '#f87171', bg: 'rgba(248,113,113,0.12)',  description: 'Shipment is on hold' },
];

const PAYMENT_OPTIONS = [
  'Cash on Delivery (COD)',
  'Credit Card',
  'Debit Card',
  'Bank Transfer',
  'PayPal',
  'Invoice (B2B)',
  'Cryptocurrency',
  'Prepaid Account',
];

export default function AdminCreateTab() {
  const [form, setForm] = useState<NewShipment>(EMPTY);
  const [created, setCreated] = useState(false);
  const [createdId, setCreatedId] = useState('');
  const [emailSent, setEmailSent] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [labelData, setLabelData] = useState<LabelData | null>(null);
  const [previewId, setPreviewId] = useState('');
  const [previewCopied, setPreviewCopied] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  const labelRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();
  const { settings } = useSiteSettings();
  const siteUrl = settings.siteUrl || 'https://swift-tracks.com';

  useEffect(() => {
    setPreviewId(generateTrackingId());
  }, []);

  const set = (k: keyof NewShipment, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const handleCopy = () => {
    navigator.clipboard.writeText(createdId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCopyLink = () => {
    const url = `${siteUrl}/track/${createdId}`;
    navigator.clipboard.writeText(url);
    addToast({ type: 'success', title: 'Link copied!', message: 'Tracking URL copied to clipboard' });
  };

  const handlePrintLabel = useCallback(() => {
    if (!labelData) return;
    // Mount the label first, then print once it is in the DOM.
    setPrintMode(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        // Un-mount label after a short delay to clean up the injected CSS.
        setTimeout(() => setPrintMode(false), 800);
      });
    });
  }, [labelData]);

  const handlePreviewCopy = () => {
    navigator.clipboard.writeText(previewId).then(() => {
      setPreviewCopied(true);
      setTimeout(() => setPreviewCopied(false), 2000);
    });
  };

  const handleRegenerateId = () => setPreviewId(generateTrackingId());

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    try {
      const id = previewId || generateTrackingId();
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      const originStr = form.originInput.trim() || form.senderCity.trim();
      const destStr = form.destinationInput.trim() || form.receiverCity.trim();
      const originCity = originStr.split(',')[0].trim();
      const originCountry = originStr.split(',')[1]?.trim() ?? '';
      const destCity = destStr.split(',')[0].trim();
      const destCountry = destStr.split(',')[1]?.trim() ?? '';

      const selectedStatus = STATUS_OPTIONS.find((s) => s.value === form.status) ?? STATUS_OPTIONS[0];

      const newShipment: Shipment = {
        id,
        sender: {
          name: form.senderName,
          address: '',
          city: form.senderCity.split(',')[0].trim(),
          country: form.senderCity.split(',')[1]?.trim() ?? '',
        },
        receiver: {
          name: form.receiverName,
          address: '',
          city: form.receiverCity.split(',')[0].trim(),
          country: form.receiverCity.split(',')[1]?.trim() ?? '',
          email: form.receiverEmail.trim() || undefined,
        },
        status: form.status as Shipment['status'],
        weight: parseFloat(form.weight) || 0,
        dimensions: form.dimensions || 'N/A',
        service: form.service,
        carrier: 'SwiftTrack',
        estimatedDelivery: form.estimatedDelivery,
        origin: { lat: 0, lng: 0, city: originCity, country: originCountry },
        destination: { lat: 0, lng: 0, city: destCity, country: destCountry },
        description: form.description,
        value: parseFloat(form.declaredValue) || 0,
        paymentMethod: form.paymentMethod,
        timeline: [{ status: form.status, timestamp, location: originStr, description: selectedStatus.description }],
      };

      const { error } = await createShipment(newShipment);

      if (error) {
        addToast({ type: 'error', title: 'Save Failed', message: error });
        setSaving(false);
        return;
      }

      // Fire customer notification email (non-blocking)
      if (form.receiverEmail.trim()) {
        sendCreationNotification(newShipment, siteUrl);
      }

      // Fire admin notification email to all admin users (non-blocking)
      sendAdminNotification(newShipment, siteUrl);

      const capturedEmail = form.receiverEmail;
      setCreatedId(id);
      setEmailSent(capturedEmail);
      setLabelData({
        trackingId: id,
        senderName: form.senderName,
        senderCity: form.senderCity,
        receiverName: form.receiverName,
        receiverCity: form.receiverCity,
        service: form.service,
        weight: form.weight,
        estimatedDelivery: form.estimatedDelivery,
        createdAt: dateStr,
      });
      setCreated(true);
      setForm(EMPTY);
      setPreviewId(generateTrackingId());
      addToast({ type: 'success', title: 'Shipment Created!', message: `${id} saved to database` });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Unexpected Error',
        message: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-[#1a3333] border border-teal-900/40 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-700 focus:border-teal-600 transition-colors';

  return (
    <div>
      {/* ShippingLabel is ONLY rendered while printMode=true — never on form submit */}
      {printMode && labelData && <ShippingLabel ref={labelRef} data={labelData} />}

      {/* PRE-GENERATED TRACKING CODE BANNER */}
      {!created && (
        <div className="bg-[#0d1f1f] border border-teal-600/40 rounded-2xl px-5 py-5 mb-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-shrink-0">
              <div className="text-teal-500 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <i className="ri-barcode-box-line" />
                Auto-Generated Tracking Code
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white font-mono font-bold text-2xl tracking-widest">{previewId}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePreviewCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border"
                    style={{
                      background: previewCopied ? 'rgba(16,185,129,0.15)' : 'rgba(13,148,136,0.12)',
                      color: previewCopied ? '#6ee7b7' : '#5eead4',
                      borderColor: previewCopied ? 'rgba(16,185,129,0.3)' : 'rgba(13,148,136,0.25)',
                    }}
                  >
                    <i className={previewCopied ? 'ri-check-line' : 'ri-file-copy-line'} />
                    {previewCopied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    onClick={handleRegenerateId}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border border-teal-900/40 bg-[#1a3333] text-teal-600 hover:text-teal-300"
                  >
                    <i className="ri-refresh-line" />
                    Regenerate
                  </button>
                </div>
              </div>
              <p className="text-teal-700 text-xs mt-2">
                <i className="ri-information-line mr-1" />
                This code will be assigned to the shipment when you submit
              </p>
            </div>
            <div className="ml-auto flex-shrink-0">
              <div className="bg-white p-2 rounded-xl">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(`${siteUrl}/track/${previewId || 'pending'}`)}&format=png`}
                  alt="QR preview"
                  width={80}
                  height={80}
                  className="block"
                />
              </div>
              <div className="text-teal-700 text-xs text-center mt-1">QR preview</div>
            </div>
          </div>
        </div>
      )}

      {/* Success banner */}
      {created && (
        <div className="bg-teal-900/30 border border-teal-600/30 rounded-2xl px-5 py-5 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-checkbox-circle-fill text-teal-400 text-xl" />
              </div>
              <div className="flex-1">
                <div className="text-teal-300 font-semibold text-sm mb-3">Shipment Created Successfully!</div>
                <div className="bg-[#0d1f1f] border border-teal-700/40 rounded-xl px-4 py-4 mb-3">
                  <div className="text-teal-600 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <i className="ri-barcode-box-line" />
                    Tracking Number
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="bg-white p-2 rounded-lg">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`${siteUrl}/track/${createdId}`)}&format=png`}
                          alt={`QR code for ${createdId}`}
                          width={100}
                          height={100}
                          className="block"
                        />
                      </div>
                      <div className="text-teal-700 text-xs text-center mt-1.5">Scan to track</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-white font-mono font-bold text-lg tracking-wider block mb-3 break-all">{createdId}</span>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={handleCopy}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                          style={{
                            background: copied ? 'rgba(16,185,129,0.2)' : 'rgba(13,148,136,0.2)',
                            color: copied ? '#6ee7b7' : '#5eead4',
                            border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'rgba(13,148,136,0.3)'}`,
                          }}
                        >
                          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
                          {copied ? 'Copied!' : 'Copy tracking number'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCopyLink}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border border-teal-900/40 bg-[#1a3333] text-teal-600 hover:text-teal-400"
                        >
                          <i className="ri-link" />
                          Copy tracking link
                        </button>
                        <button
                          type="button"
                          onClick={handlePrintLabel}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border border-teal-900/40 bg-[#1a3333] text-teal-400 hover:text-teal-200"
                        >
                          <i className="ri-printer-line" />
                          Print shipping label
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {emailSent && (
                  <div className="flex items-center gap-2 bg-teal-950/50 border border-teal-800/40 rounded-xl px-4 py-3">
                    <div className="w-7 h-7 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-check-line text-teal-400 text-sm" />
                    </div>
                    <div>
                      <div className="text-teal-400 text-xs font-semibold">Confirmation email sent to:</div>
                      <div className="text-white text-sm font-medium">{emailSent}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-1 text-teal-500 text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-ping" />
                      Delivered
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => { setCreated(false); setPrintMode(false); }}
              className="text-teal-600 hover:text-teal-300 cursor-pointer flex-shrink-0 mt-1"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleCreate} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sender */}
          <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6">
            <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider mb-5">Sender</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Full Name</label>
                <input required value={form.senderName} onChange={(e) => set('senderName', e.target.value)} placeholder="Amazon Fulfillment" className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">City &amp; Country</label>
                <input required value={form.senderCity} onChange={(e) => set('senderCity', e.target.value)} placeholder="London, UK" className={inputCls} />
              </div>
            </div>
          </div>

          {/* Receiver */}
          <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6">
            <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider mb-5">Receiver</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Full Name</label>
                <input required value={form.receiverName} onChange={(e) => set('receiverName', e.target.value)} placeholder="John Mitchell" className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">City &amp; Country</label>
                <input
                  required
                  value={form.receiverCity}
                  onChange={(e) => {
                    set('receiverCity', e.target.value);
                    if (!form.destinationInput) set('destinationInput', e.target.value);
                  }}
                  placeholder="Tokyo, Japan"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2 flex items-center gap-1.5">
                  Email Address
                  <span className="text-teal-700 font-normal normal-case">(confirmation will be sent here)</span>
                </label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-teal-600 text-sm pointer-events-none" />
                  <input
                    type="email"
                    value={form.receiverEmail}
                    onChange={(e) => set('receiverEmail', e.target.value)}
                    placeholder="john.mitchell@email.com"
                    className={inputCls + ' pl-10'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Route & Map */}
        <div className="bg-[#0d2020] border border-teal-700/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-map-2-line text-teal-400 text-lg" />
            </div>
            <div>
              <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider">Route &amp; Map Display</h3>
              <p className="text-teal-700 text-xs mt-0.5">These two points define the route arc shown on the customer tracking map</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-2 flex items-center gap-1.5">
                <i className="ri-map-pin-fill text-green-400" />
                Origin / Pickup Location
                <span className="text-teal-700 font-normal normal-case ml-1">(start pin on map)</span>
              </label>
              <div className="relative">
                <i className="ri-flight-takeoff-line absolute left-4 top-1/2 -translate-y-1/2 text-teal-600 text-sm pointer-events-none" />
                <input
                  value={form.originInput}
                  onChange={(e) => set('originInput', e.target.value)}
                  placeholder={form.senderCity || 'London, United Kingdom'}
                  className={inputCls + ' pl-10'}
                />
              </div>
              <p className="text-teal-800 text-xs mt-1.5">
                {form.originInput
                  ? <span className="text-teal-500"><i className="ri-check-line mr-1" />Custom origin: <strong>{form.originInput}</strong></span>
                  : form.senderCity
                    ? <span><i className="ri-information-line mr-1" />Using sender city: <strong className="text-teal-500">{form.senderCity}</strong></span>
                    : 'Enter "City, Country" — e.g. Paris, France'}
              </p>
            </div>
            <div>
              <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-2 flex items-center gap-1.5">
                <i className="ri-map-pin-fill text-red-400" />
                Destination / Delivery Location
                <span className="text-teal-700 font-normal normal-case ml-1">(end pin on map)</span>
              </label>
              <div className="relative">
                <i className="ri-flight-land-line absolute left-4 top-1/2 -translate-y-1/2 text-teal-600 text-sm pointer-events-none" />
                <input
                  value={form.destinationInput}
                  onChange={(e) => set('destinationInput', e.target.value)}
                  placeholder={form.receiverCity || 'New York, United States'}
                  className={inputCls + ' pl-10'}
                />
              </div>
              <p className="text-teal-800 text-xs mt-1.5">
                {form.destinationInput
                  ? <span className="text-teal-500"><i className="ri-check-line mr-1" />Custom destination: <strong>{form.destinationInput}</strong></span>
                  : form.receiverCity
                    ? <span><i className="ri-information-line mr-1" />Using receiver city: <strong className="text-teal-500">{form.receiverCity}</strong></span>
                    : 'Enter "City, Country" — e.g. Dubai, UAE'}
              </p>
            </div>
          </div>
          {(form.originInput || form.senderCity) && (form.destinationInput || form.receiverCity) && (
            <div className="mt-4 flex items-center gap-3 bg-teal-950/50 border border-teal-800/30 rounded-xl px-4 py-3">
              <i className="ri-route-line text-teal-400 text-sm flex-shrink-0" />
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <span className="flex items-center gap-1 text-green-400 font-semibold">
                  <i className="ri-map-pin-fill" />{form.originInput || form.senderCity}
                </span>
                <span className="text-teal-700">— — — — →</span>
                <span className="flex items-center gap-1 text-red-400 font-semibold">
                  <i className="ri-map-pin-fill" />{form.destinationInput || form.receiverCity}
                </span>
                <span className="ml-auto text-teal-600 italic">Route arc will display on customer tracking map</span>
              </div>
            </div>
          )}
        </div>

        {/* Package Details */}
        <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6">
          <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider mb-5">Package Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Contents</label>
              <input required value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Electronics - Laptop" className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Weight (kg)</label>
              <input required type="number" step="0.1" value={form.weight} onChange={(e) => set('weight', e.target.value)} placeholder="2.5" className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Dimensions (cm)</label>
              <input value={form.dimensions} onChange={(e) => set('dimensions', e.target.value)} placeholder="30x20x15" className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Service Tier</label>
              <select value={form.service} onChange={(e) => set('service', e.target.value)} className={inputCls + ' cursor-pointer'}>
                <option>Economy</option>
                <option>Standard</option>
                <option>Express</option>
                <option>Air Freight</option>
                <option>Ocean Cargo</option>
                <option>Same Day</option>
                <option>Late Night</option>
                <option>Priority</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Est. Delivery</label>
              <input required type="date" value={form.estimatedDelivery} onChange={(e) => set('estimatedDelivery', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-2">Declared Value (USD)</label>
              <input type="number" step="0.01" value={form.declaredValue} onChange={(e) => set('declaredValue', e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
          </div>

          {/* Delivery Status */}
          <div className="mt-6">
            <label className="text-xs text-teal-500 font-semibold uppercase tracking-wide block mb-3 flex items-center gap-1.5">
              <i className="ri-truck-line" />
              Initial Delivery Status
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {STATUS_OPTIONS.map((opt) => {
                const isSelected = form.status === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => set('status', opt.value)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer text-left"
                    style={{
                      borderColor: isSelected ? opt.color : 'rgba(13,148,136,0.2)',
                      background: isSelected ? opt.bg : '#1a3333',
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: isSelected ? opt.bg : 'rgba(255,255,255,0.04)' }}
                    >
                      <i
                        className={`${opt.icon} text-base`}
                        style={{ color: isSelected ? opt.color : '#4d9e9e' }}
                      />
                    </div>
                    <div className="min-w-0">
                      <div
                        className="text-xs font-semibold leading-tight"
                        style={{ color: isSelected ? opt.color : '#5eead4' }}
                      >
                        {opt.label}
                      </div>
                      <div className="text-teal-800 text-xs mt-0.5 leading-tight truncate">{opt.description}</div>
                    </div>
                    {isSelected && (
                      <i className="ri-checkbox-circle-fill ml-auto flex-shrink-0 text-sm" style={{ color: opt.color }} />
                    )}
                  </button>
                );
              })}
            </div>
            {(() => {
              const sel = STATUS_OPTIONS.find((s) => s.value === form.status);
              return sel ? (
                <div className="mt-3 flex items-center gap-2 text-xs text-teal-600">
                  <i className="ri-information-line" />
                  Shipment will be created with status:{' '}
                  <span className="font-semibold" style={{ color: sel.color }}>{sel.label}</span>
                </div>
              ) : null;
            })()}
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6">
          <h3 className="font-semibold text-teal-300 text-sm uppercase tracking-wider mb-5 flex items-center gap-2">
            <i className="ri-secure-payment-line text-teal-400" />
            Payment Method
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PAYMENT_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => set('paymentMethod', option)}
                className={`flex flex-col items-center gap-2 px-3 py-4 rounded-xl border text-xs font-medium transition-all cursor-pointer ${
                  form.paymentMethod === option
                    ? 'border-teal-500 bg-teal-900/40 text-teal-300'
                    : 'border-teal-900/40 bg-[#1a3333] text-teal-600 hover:border-teal-700 hover:text-teal-400'
                }`}
              >
                <i className={`text-xl ${
                  option === 'Cash on Delivery (COD)' ? 'ri-money-dollar-circle-line' :
                  option === 'Credit Card' ? 'ri-bank-card-line' :
                  option === 'Debit Card' ? 'ri-bank-card-2-line' :
                  option === 'Bank Transfer' ? 'ri-building-line' :
                  option === 'PayPal' ? 'ri-paypal-line' :
                  option === 'Invoice (B2B)' ? 'ri-file-text-line' :
                  option === 'Cryptocurrency' ? 'ri-bit-coin-line' :
                  'ri-wallet-line'
                }`} />
                <span className="text-center leading-tight">{option}</span>
                {form.paymentMethod === option && (
                  <i className="ri-checkbox-circle-fill text-teal-400 text-sm" />
                )}
              </button>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-teal-600">
            <i className="ri-information-line" />
            Selected: <span className="text-teal-300 font-semibold">{form.paymentMethod}</span>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full py-4 rounded-2xl text-sm font-semibold text-white transition-opacity hover:opacity-90 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          style={{ background: 'var(--primary)' }}
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Saving Shipment...
            </>
          ) : (
            <>
              <i className="ri-add-circle-line" />
              Create Shipment &amp; Generate Tracking Number
            </>
          )}
        </button>
      </form>
    </div>
  );
}
